import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Tabs,
  Tab,
  Badge,
  CircularProgress,
  Paper,
  Alert,
  Link
} from '@mui/material';
import {
  Logout,
  Refresh,
  Coffee,
  WifiOff,
  CloudOff,
  Wifi
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useOrders } from '../context/OrdersContext';
import { useNavigate } from 'react-router-dom';
import OrderCard from './OrderCard';
import NewOrderModal from './NewOrderModal';
import OrderDetailsModal from './OrderDetailsModal';
import { unlockNotificationAudio } from '../services/notifications';

const OrdersBoard = () => {
  const { user, logout } = useAuth();
  const {
    orders,
    isLoading,
    newOrderNotification,
    newOrdersQueue,
    connectionStatus,
    dismissNewOrderNotification
  } = useOrders();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  useEffect(() => {
    // Автоматически открываем/закрываем модальное окно в зависимости от наличия заказов в очереди
    if (newOrderNotification) {
      setModalOpen(true);
    } else {
      setModalOpen(false);
    }
  }, [newOrderNotification]);

  // Разблокировка звука при первом жесте на доске (если вошли без клика «Войти» в этой сессии)
  useEffect(() => {
    const onFirstGesture = () => {
      void unlockNotificationAudio();
      window.removeEventListener('pointerdown', onFirstGesture, { capture: true });
      window.removeEventListener('keydown', onFirstGesture, { capture: true });
    };
    window.addEventListener('pointerdown', onFirstGesture, { capture: true });
    window.addEventListener('keydown', onFirstGesture, { capture: true });
    return () => {
      window.removeEventListener('pointerdown', onFirstGesture, { capture: true });
      window.removeEventListener('keydown', onFirstGesture, { capture: true });
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleModalClose = () => {
    setModalOpen(false);
    dismissNewOrderNotification();
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setDetailsModalOpen(true);
  };

  const handleDetailsModalClose = () => {
    setDetailsModalOpen(false);
    setSelectedOrder(null);
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const filterOrdersByTab = () => {
    switch (currentTab) {
      case 0: // Все
        return orders;
      case 1: // Новые
        return orders.filter(order => 
          order.status === 'pending' || 
          order.status === 'new' || 
          order.status === 'awaiting_confirmation'
        );
      case 2: // Подтвержденные
        return orders.filter(order => 
          order.status === 'paid' || 
          order.status === 'confirmed' || 
          order.status === 'preparing'
        );
      case 3: // Готовые
        return orders.filter(order => order.status === 'ready' || order.status === 'completed');
      default:
        return orders;
    }
  };

  const getOrdersCount = (status) => {
    if (status === 'all') return orders.length;
    return orders.filter(order => order.status === status).length;
  };

  const filteredOrders = filterOrdersByTab();

  return (
    <>
      <AppBar position="sticky">
        <Toolbar>
          <Coffee sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Quickly Coffee - Панель баристы
          </Typography>
          
          <Typography variant="body1" sx={{ mr: 2 }}>
            Устройство #{user?.deviceId || 'N/A'}
          </Typography>

          <IconButton
            color="inherit"
            onClick={() => window.location.reload()}
            sx={{ mr: 1 }}
            title="Перезагрузить страницу"
          >
            <Refresh />
          </IconButton>

          <Button
            color="inherit"
            startIcon={<Logout />}
            onClick={handleLogout}
          >
            Выход
          </Button>
        </Toolbar>
      </AppBar>

      {connectionStatus?.status === 'reconnecting' && (
        <Alert
          severity="warning"
          icon={<WifiOff />}
          sx={{ borderRadius: 0 }}
        >
          Связь с сервером потеряна. Идёт попытка переподключения ({connectionStatus.attempt}/{connectionStatus.maxAttempts})…
        </Alert>
      )}

      {connectionStatus?.status === 'reconnected' && (
        <Alert severity="success" icon={<Wifi />} sx={{ borderRadius: 0 }}>
          Связь с сервером восстановлена
        </Alert>
      )}

      {connectionStatus?.status === 'reconnect_failed' && (
        <Alert
          severity="error"
          icon={<CloudOff />}
          sx={{ borderRadius: 0 }}
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Обновить страницу
            </Button>
          }
        >
          Не удалось восстановить связь с сервером. Обновите страницу или напишите в Telegram:{' '}
          <Link href="https://t.me/nshvora" target="_blank" rel="noopener noreferrer" color="inherit" sx={{ fontWeight: 600 }}>
            @nshvora
          </Link>
        </Alert>
      )}

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box mb={3}>
          <Typography variant="h4" gutterBottom>
            Заказы
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Управление текущими заказами
          </Typography>
        </Box>

        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab
              label={
                <Badge badgeContent={getOrdersCount('all')} color="primary">
                  <Box px={1}>Все</Box>
                </Badge>
              }
            />
            <Tab
              label={
                <Badge 
                  badgeContent={
                    getOrdersCount('pending') + 
                    getOrdersCount('new') + 
                    getOrdersCount('awaiting_confirmation')
                  } 
                  color="error"
                >
                  <Box px={1}>Новые</Box>
                </Badge>
              }
            />
            <Tab
              label={
                <Badge 
                  badgeContent={
                    getOrdersCount('paid') + 
                    getOrdersCount('confirmed') + 
                    getOrdersCount('preparing')
                  } 
                  color="warning"
                >
                  <Box px={1}>Подтвержденные</Box>
                </Badge>
              }
            />
            <Tab
              label={
                <Badge badgeContent={getOrdersCount('ready')} color="success">
                  <Box px={1}>Готовые</Box>
                </Badge>
              }
            />
          </Tabs>
        </Paper>

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress size={60} />
          </Box>
        ) : filteredOrders.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Нет заказов в этой категории
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {filteredOrders.map((order) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={order.id}>
                <OrderCard order={order} onClick={() => handleOrderClick(order)} />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      <NewOrderModal
        order={newOrderNotification}
        open={modalOpen}
        onClose={handleModalClose}
      />

      <OrderDetailsModal
        order={selectedOrder}
        open={detailsModalOpen}
        onClose={handleDetailsModalClose}
      />
    </>
  );
};

export default OrdersBoard;

